import {
  NgModule,
  ModuleWithProviders,
  Optional,
  SkipSelf,
  APP_INITIALIZER
} from '@angular/core';
import { AppUtilService } from './utils/app-util.service';
import { MessageService } from './services/message.service';
import { AppCacheService } from './services/cache/app-cache.service';
import { AuthService } from './services/auth.service';
import { HttpService } from './services/http/http.service';
import { AuthGuard } from './guards/auth.guard';
import { UnauthGuard } from './guards/unauth.guard';
import { CanOrderGuard } from './guards/can-order.guard';
import {CanSubscribeGuard} from './guards/can-subscribe.guard';
import {isAssociationExists} from './guards/is-association-exists.guard';
import { MobileDeviceGuard } from './guards/mobile-device.guard';
import { ParmedApiService } from './api/parmed-api.service';
import { StaticService } from './services/static/static.service';
import { AppConfigService } from './services/init/app-config.service';
import { InitFactory } from './services/init/app-init.factory';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HeaderInterceptor } from './interceptors/header.interceptor';
import {
  DeviceDetectorService,
  DeviceDetectorModule
} from 'ngx-device-detector';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { CacheModule, CacheLoader, CACHE } from '@ngx-cache/core';
import { BrowserCacheModule } from '@ngx-cache/platform-browser';
import { CacheFactory } from './services/cache/app-cache.factory';
import { ExportExcelService } from './services/export-excel.service';
import { DialogService } from './services/dialog/dialog.service';
import { ConfirmDialogComponent } from './services/dialog/confirm-dialog/confirm-dialog.component';
import { MaterialModule } from '../shared/modules/material.module';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule, DatePipe } from '@angular/common';

import { OAuthModule } from 'angular-oauth2-oidc';
import { RouteService } from './services/route.service';
import { CanSubmitOrderGuard } from './guards/can-submit-order.guard';
import { CanReviewOrderGuard } from './guards/can-review-order.guard';
import { OktaAuthService } from './services/okta-auth.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { AppRouteHandlerService } from './services/route/app-route-handler.service';
import { AnalyticsService } from './services/analytics.service';
import { ScriptService } from './services/script.service';
import { DesktopDeviceGuard } from './guards/desktop-device.guard';
import { CacheResolverFactory } from './services/cache/cache-resolver.factory';
import { CanPayGuard } from './guards/can-pay.guard';
import { CanAccessDscsa } from './guards/can-access-dscsa.guard';
import { CanOrderAndPay } from './guards/can-order-and-pay.guard';
import { CanCheckOrderHistory } from './guards/can-check-histoty.guard';

@NgModule({
    declarations: [ConfirmDialogComponent],
    imports: [
        CommonModule,
        BrowserModule,
        MaterialModule,
        CacheModule.forRoot({
            provide: CacheLoader,
            useFactory: CacheFactory
        }),
        BrowserCacheModule.forRoot([
            {
                provide: CACHE,
                useClass: CacheResolverFactory
            }
        ]),
        DeviceDetectorModule.forRoot(),
        OAuthModule.forRoot()
    ],
    providers: [
        HttpService,
        AppConfigService,
        AppRouteHandlerService,
        ParmedApiService,
        OktaAuthService,
        AuthService,
        AppCacheService,
        StaticService,
        MessageService,
        DialogService,
        AuthGuard,
        UnauthGuard,
        CanOrderGuard,
        CanSubscribeGuard,
        isAssociationExists,
        CanReviewOrderGuard,
        CanSubmitOrderGuard,
        MobileDeviceGuard,
        DesktopDeviceGuard,
        CanPayGuard,
        CanAccessDscsa,
        CanOrderAndPay,
        CanCheckOrderHistory,
        AppUtilService,
        ExportExcelService,
        AnalyticsService,
        DatePipe,
        GoogleAnalyticsService,
        RouteService,
        ScriptService,
        {
            provide: APP_INITIALIZER,
            useFactory: InitFactory,
            deps: [AppConfigService, AppCacheService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderInterceptor,
            deps: [
                AppConfigService,
                AuthService,
                DeviceDetectorService,
                AppUtilService,
                AppRouteHandlerService
            ],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            deps: [AuthService, StaticService, MessageService],
            multi: true
        }
    ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule
  ) {
    if (parentModule) {
      throw new Error(
        'CoreModule is already loaded. Import it in the AppModule only'
      );
    }
  }

  static forRoot(): ModuleWithProviders<any> {
    return {
      ngModule: CoreModule,
      providers: [
        HttpService,
        AppConfigService,
        AppRouteHandlerService,
        ParmedApiService,
        OktaAuthService,
        AuthService,
        AppCacheService,
        StaticService,
        MessageService,
        AppUtilService,
        DialogService,
        RouteService,
        AnalyticsService,
        DatePipe,
        ScriptService
      ]
    };
  }
}
